import { useAuth } from '@/core/contexts/AuthContext';
import {
    jwtHasUaepassClaim,
    readConvertedLeadFlag,
    shouldEnterAdhaJourney,
} from '@/core/constants/adhaJourney';
import { EMIRATES_ID_AUTH_MESSAGES } from '@/modules/auth/constants/emiratesIdAuth';
import { useAdhaLogin } from '@/modules/auth/hooks/useAdhaLogin';
import { ADHA_LOGIN_VERIFICATION_PROCESS_TYPE, authService, readSendOtpJwtToken } from '@/modules/auth/services/authService';
import {
    trackEmiratesIdEmailOtpVerifyClick,
    trackEmiratesIdEmailOtpVerifyError,
    trackEmiratesIdEmailOtpVerifySuccess,
    trackEmiratesIdLoginStepView,
    trackEmiratesIdMobileOtpVerifyClick,
    trackEmiratesIdMobileOtpVerifyError,
    trackEmiratesIdMobileOtpVerifySuccess,
    trackEmiratesIdOtpResendClick,
    trackEmiratesIdOtpResendError,
    trackEmiratesIdSubmitClick,
    trackEmiratesIdSubmitError,
} from '@/core/services/trackDynatraceCatalog';
import { hydrateNormalCustomerFromProfile } from '@/modules/auth/services/hydrateNormalCustomerFromProfile';
import { useCallback, useEffect, useRef, useState } from 'react';

export type EmiratesIdLoginStep = 'EMIRATES_ID' | 'MOBILE_OTP' | 'EMAIL_OTP';

function getEmiratesIdApiError(err: unknown, fallback: string): string {
    const error = err as {
        message?: string;
        details?: { message?: string };
        response?: {
            message?: string;
            details?: { message?: string };
            data?: { message?: string; details?: { message?: string } };
        };
    };

    return (
        error.response?.details?.message ||
        error.response?.data?.details?.message ||
        error.response?.data?.message ||
        error.response?.message ||
        error.details?.message ||
        error.message ||
        fallback
    );
}

function formatEmiratesIdForApi(value: string): string {
    const digits = value.replace(/\D/g, '');
    return `${digits.substring(0, 3)}-${digits.substring(3, 7)}-${digits.substring(7, 14)}-${digits.substring(14, 15)}`;
}

/**
 * Mobile Emirates ID login — same 3 API steps as web LoginDialog.
 * Does not change the web dialog.
 */
export function useEmiratesIdLogin() {
    const [step, setStep] = useState<EmiratesIdLoginStep>('EMIRATES_ID');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [tempJwtToken, setTempJwtToken] = useState('');
    const [tempEmail, setTempEmail] = useState('');
    const [tempPhoneNumber, setTempPhoneNumber] = useState('');
    const [otpJwtToken, setOtpJwtToken] = useState('');
    const { login: authLogin } = useAuth();
    const { completeAdhaLoginWithToken } = useAdhaLogin();
    const stepViewTrackedRef = useRef<Record<string, boolean>>({});

    useEffect(() => {
        if (stepViewTrackedRef.current[step]) {
            return;
        }
        stepViewTrackedRef.current[step] = true;
        trackEmiratesIdLoginStepView(step);
    }, [step]);

    const reset = useCallback(() => {
        setStep('EMIRATES_ID');
        setIsLoading(false);
        setError(null);
        setSuccessMessage(null);
        setTempJwtToken('');
        setTempEmail('');
        setTempPhoneNumber('');
        setOtpJwtToken('');
        stepViewTrackedRef.current = {};
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const clearSuccessMessage = useCallback(() => {
        setSuccessMessage(null);
    }, []);

    const submitEmiratesId = useCallback(async (emiratesIdValue: string): Promise<boolean> => {
        const digits = emiratesIdValue.replace(/\D/g, '');
        if (isLoading || digits.length !== 15) {
            return false;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        trackEmiratesIdSubmitClick();

        try {
            const formattedEmiratesId = formatEmiratesIdForApi(digits);
            const signInRes = await authService.loginWithEmiratesId(formattedEmiratesId);
            if (signInRes.status !== 'success') {
                throw new Error(
                    (signInRes as { details?: { message?: string } }).details?.message ||
                    signInRes.message ||
                    EMIRATES_ID_AUTH_MESSAGES.SIGN_IN_FAILED,
                );
            }

            const token =
                signInRes.data?.jwtToken ||
                (signInRes as { details?: { jwtToken?: string }; jwtToken?: string }).details?.jwtToken ||
                (signInRes as { jwtToken?: string }).jwtToken;
            const emailVal =
                signInRes.data?.email ||
                (signInRes as { details?: { email?: string }; email?: string }).details?.email ||
                (signInRes as { email?: string }).email ||
                '';
            const phoneVal =
                signInRes.data?.phoneNumber ||
                (signInRes as { details?: { phoneNumber?: string }; phoneNumber?: string }).details?.phoneNumber ||
                (signInRes as { phoneNumber?: string }).phoneNumber ||
                '';

            if (!token) {
                throw new Error(EMIRATES_ID_AUTH_MESSAGES.SIGN_IN_FAILED);
            }

            const otpRes = await authService.sendEmiratesIdOtp(token, 'SMS', { mobileNumber: phoneVal });
            if (otpRes.status !== 'success') {
                throw new Error(
                    (otpRes as { details?: { message?: string } }).details?.message ||
                    otpRes.message ||
                    EMIRATES_ID_AUTH_MESSAGES.SMS_OTP_SEND_FAILED,
                );
            }

            const sendOtpToken = readSendOtpJwtToken(otpRes);
            if (!sendOtpToken) {
                throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_OTP_TOKEN);
            }

            setTempJwtToken(token);
            setTempEmail(emailVal);
            setTempPhoneNumber(phoneVal);
            setOtpJwtToken(sendOtpToken);
            setStep('MOBILE_OTP');
            setSuccessMessage(otpRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);
            return true;
        } catch (err) {
            const message = getEmiratesIdApiError(err, EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR);
            setError(message);
            trackEmiratesIdSubmitError(message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [isLoading]);

    const resendMobileOtp = useCallback(async (): Promise<boolean> => {
        if (isLoading || !tempJwtToken) {
            return false;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        trackEmiratesIdOtpResendClick('mobile');

        try {
            const otpRes = await authService.sendEmiratesIdOtp(tempJwtToken, 'SMS', { mobileNumber: tempPhoneNumber });
            if (otpRes.status !== 'success') {
                throw new Error(
                    (otpRes as { details?: { message?: string } }).details?.message ||
                    otpRes.message ||
                    EMIRATES_ID_AUTH_MESSAGES.SMS_OTP_RESEND_FAILED,
                );
            }

            const sendOtpToken = readSendOtpJwtToken(otpRes);
            if (!sendOtpToken) {
                throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_OTP_TOKEN);
            }

            setOtpJwtToken(sendOtpToken);
            setSuccessMessage(otpRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);
            return true;
        } catch (err) {
            const message = getEmiratesIdApiError(err, EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR);
            setError(message);
            trackEmiratesIdOtpResendError('mobile', message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, tempJwtToken, tempPhoneNumber]);

    const verifyMobileOtp = useCallback(async (otp: string): Promise<boolean> => {
        if (isLoading || otp.length !== 4) {
            return false;
        }

        trackEmiratesIdMobileOtpVerifyClick();

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const verifyRes = await authService.verifyEmiratesIdOtp(
                otpJwtToken,
                otp,
                ADHA_LOGIN_VERIFICATION_PROCESS_TYPE,
            );
            if (verifyRes.status !== 'success') {
                throw new Error(
                    (verifyRes as { details?: { message?: string } }).details?.message ||
                    verifyRes.message ||
                    EMIRATES_ID_AUTH_MESSAGES.SMS_OTP_VERIFY_FAILED,
                );
            }

            /*
            // Commenting out email verification step — Emirates ID login
            // now completes directly after mobile OTP verification (matches
            // web's LoginDialog.web.tsx handleMobileOtpVerify, which does
            // the same bypass; verifyRes already carries the final
            // jwtToken/user, same as verifyEmailOtp used to provide).
            const otpRes = await authService.sendEmiratesIdOtp(tempJwtToken, 'Email', { email: tempEmail });
            if (otpRes.status !== 'success') {
                throw new Error(
                    (otpRes as { details?: { message?: string } }).details?.message ||
                    otpRes.message ||
                    EMIRATES_ID_AUTH_MESSAGES.EMAIL_OTP_SEND_FAILED,
                );
            }

            const txId = readTransactionId(otpRes as { data?: { transactionId?: string }; details?: { transactionId?: string }; transactionId?: string });
            if (!txId) {
                throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_TRANSACTION_ID);
            }

            setTransactionId(txId);
            setStep('EMAIL_OTP');
            setSuccessMessage(verifyRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);
            return true;
            */

            const finalToken =
                verifyRes.data?.jwtToken ||
                (verifyRes as { details?: { jwtToken?: string }; jwtToken?: string }).details?.jwtToken ||
                (verifyRes as { jwtToken?: string }).jwtToken;

            if (!finalToken) {
                throw new Error(EMIRATES_ID_AUTH_MESSAGES.SMS_OTP_VERIFY_FAILED);
            }

            const loginSuccessMessage = verifyRes.message || EMIRATES_ID_AUTH_MESSAGES.LOGIN_SUCCESS;

            const convertedLead = readConvertedLeadFlag(verifyRes);
            const enterAdha = shouldEnterAdhaJourney({
                convertedLead,
                hasUaepassClaim: jwtHasUaepassClaim(finalToken),
            });

            trackEmiratesIdMobileOtpVerifySuccess();

            if (enterAdha) {
                await completeAdhaLoginWithToken(finalToken);
                setSuccessMessage(loginSuccessMessage);
                return true;
            }

            await hydrateNormalCustomerFromProfile(finalToken, 'emirates_id', convertedLead);
            authLogin();
            setSuccessMessage(loginSuccessMessage);
            return true;
        } catch (err) {
            const message = getEmiratesIdApiError(err, EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR);
            setError(message);
            trackEmiratesIdMobileOtpVerifyError(message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [authLogin, completeAdhaLoginWithToken, isLoading, otpJwtToken]);

    const resendEmailOtp = useCallback(async (): Promise<boolean> => {
        if (isLoading || !tempJwtToken) {
            return false;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        trackEmiratesIdOtpResendClick('email');

        try {
            const otpRes = await authService.sendEmiratesIdOtp(tempJwtToken, 'Email', { email: tempEmail });
            if (otpRes.status !== 'success') {
                throw new Error(
                    (otpRes as { details?: { message?: string } }).details?.message ||
                    otpRes.message ||
                    EMIRATES_ID_AUTH_MESSAGES.EMAIL_OTP_RESEND_FAILED,
                );
            }

            const sendOtpToken = readSendOtpJwtToken(otpRes);
            if (!sendOtpToken) {
                throw new Error(EMIRATES_ID_AUTH_MESSAGES.MISSING_OTP_TOKEN);
            }

            setOtpJwtToken(sendOtpToken);
            setSuccessMessage(otpRes.message || EMIRATES_ID_AUTH_MESSAGES.OTP_SENT_SUCCESS);
            return true;
        } catch (err) {
            const message = getEmiratesIdApiError(err, EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR);
            setError(message);
            trackEmiratesIdOtpResendError('email', message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, tempEmail, tempJwtToken]);

    const verifyEmailOtp = useCallback(async (otp: string): Promise<boolean> => {
        if (isLoading || otp.length !== 4) {
            return false;
        }

        trackEmiratesIdEmailOtpVerifyClick();

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const verifyRes = await authService.verifyEmiratesIdOtp(
                otpJwtToken,
                otp,
                ADHA_LOGIN_VERIFICATION_PROCESS_TYPE,
            );
            if (verifyRes.status !== 'success') {
                throw new Error(
                    (verifyRes as { details?: { message?: string } }).details?.message ||
                    verifyRes.message ||
                    EMIRATES_ID_AUTH_MESSAGES.EMAIL_OTP_VERIFY_FAILED,
                );
            }

            const finalToken =
                verifyRes.data?.jwtToken ||
                (verifyRes as { details?: { jwtToken?: string }; jwtToken?: string }).details?.jwtToken ||
                (verifyRes as { jwtToken?: string }).jwtToken;

            if (!finalToken) {
                throw new Error(EMIRATES_ID_AUTH_MESSAGES.EMAIL_OTP_VERIFY_FAILED);
            }

            const loginSuccessMessage = verifyRes.message || EMIRATES_ID_AUTH_MESSAGES.LOGIN_SUCCESS;

            const convertedLead = readConvertedLeadFlag(verifyRes);
            const enterAdha = shouldEnterAdhaJourney({
                convertedLead,
                hasUaepassClaim: jwtHasUaepassClaim(finalToken),
            });

            trackEmiratesIdEmailOtpVerifySuccess();

            if (enterAdha) {
                await completeAdhaLoginWithToken(finalToken);
                setSuccessMessage(loginSuccessMessage);
                return true;
            }

            await hydrateNormalCustomerFromProfile(finalToken, 'emirates_id', convertedLead);
            authLogin();
            setSuccessMessage(loginSuccessMessage);
            return true;
        } catch (err) {
            const message = getEmiratesIdApiError(err, EMIRATES_ID_AUTH_MESSAGES.GENERIC_ERROR);
            setError(message);
            trackEmiratesIdEmailOtpVerifyError(message);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [authLogin, completeAdhaLoginWithToken, isLoading, otpJwtToken]);

    return {
        step,
        isLoading,
        error,
        successMessage,
        tempEmail,
        tempPhoneNumber,
        submitEmiratesId,
        verifyMobileOtp,
        verifyEmailOtp,
        resendMobileOtp,
        resendEmailOtp,
        reset,
        clearError,
        clearSuccessMessage,
    };
}
